import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import { UserAvatar } from '../atoms/UserAvatar';
import type { StoredAccount } from '../../../types/auth';

interface AccountListItemProps {
  account: StoredAccount;
  isCurrent: boolean;
  isExpired: boolean;
  onClick: (email: string) => void;
  disabled?: boolean;
}

/**
 * Molecule: Single account list item
 * Uses color coding to indicate account status:
 * - Gold border: Current account
 * - Red border: Expired account
 * - Default: Other accounts
 *
 * @example
 * ```tsx
 * <AccountListItem
 *   account={storedAccount}
 *   isCurrent={false}
 *   isExpired={false}
 *   onClick={(email) => handleSwitch(email)}
 * />
 * ```
 */
export const AccountListItem: React.FC<AccountListItemProps> = ({
  account,
  isCurrent,
  isExpired,
  onClick,
  disabled = false,
}) => {
  // Determine border color based on status
  const getBorderColor = () => {
    if (isExpired) return 'error.main';
    if (isCurrent) return 'warning.main';
    return 'transparent';
  };

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => onClick(account.email)}
        disabled={disabled}
        data-testid={`account-item-${account.email}`}
        sx={{
          py: 2,
          pr: 2,
          borderLeft: 4,
          borderLeftColor: getBorderColor(),
          borderLeftStyle: 'solid',
        }}
      >
        <ListItemAvatar>
          <UserAvatar
            firstName={account.firstName}
            lastName={account.lastName}
            variant={isCurrent ? 'current' : 'inactive'}
          />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {account.firstName} {account.lastName}
            </Typography>
          }
          secondary={
            <>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {account.email}
              </Typography>
              {account.currentOrganisationName && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {account.currentOrganisationName}
                </Typography>
              )}
            </>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};
